import { Box, Button, Paper, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import {
    Alert,
    ControlledDatePicker,
    ControlledTextField,
    Form,
    palette,
    useHookForm,
} from "@powerdiary/components-library/lib";
import { useEncryptToken } from "../../hooks/useEncryptToken";
import { useNavigate } from "react-router";
import { defaultValues, authenticationSchema, AuthenticationType } from "./FormAuthentication.helpers";
import { useClientPortalConfigContext } from "../../../../context";
import { useEffect, useState } from "react";
import { ROUTES } from "../../../../routes";
import Content from "../../../Layout/Content";
import { AuthenticationMethod, ValidateTokenRequest } from "types/generated/models";

interface IFormAuthenticationProps {
    authenticationErrorMessage: string;
    publicId: string;
    formDataNumber: string;
    authenticationMethod?: AuthenticationMethod;
    cultureDateFormat?: string;
}

export default function FormAuthentication({
    authenticationErrorMessage,
    publicId,
    formDataNumber,
    authenticationMethod,
    cultureDateFormat,
}: Readonly<IFormAuthenticationProps>) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
    const { encryptToken, isPending } = useEncryptToken();
    const navigate = useNavigate();
    const { clientPortalGuid } = useClientPortalConfigContext();

    const isDobAuth = authenticationMethod === AuthenticationMethod.DateOfBirth;
    const labelText = isDobAuth ? "Enter your date of birth" : "Enter your email address or mobile number";
    const [pickerOpen, setPickerOpen] = useState(false);

    const form = useHookForm({
        defaultValues: defaultValues,
        schema: authenticationSchema,
    });

    useEffect(() => {
        if (authenticationErrorMessage) {
            form.setError("credentials", {
                type: "server",
                message: authenticationErrorMessage,
            });
        }
    }, [authenticationErrorMessage, form]);

    const handleSubmit = async (authentication: AuthenticationType) => {
        try {
            const token = await encryptToken({ token: authentication.credentials } as ValidateTokenRequest);
            await navigate(
                `/clientportal/${clientPortalGuid}/${ROUTES.PUBLIC_FORM.urlPath}/${publicId}/${formDataNumber}?token=${token}`
            );
        } catch {
            form.setError("credentials", {
                type: "manual",
                message:
                    "The details you have entered are incorrect. Please try again, or contact the clinic for assistance.",
            });
        }
    };

    return (
        <Content dataTestIdSuffix="custom_form_authentication">
            <Stack spacing={2} p={isMobile ? 2 : 4}>
                <Typography variant="paragraphLargeBold" color={palette.neutral[900]}>
                    Secure Client Form
                </Typography>
                <Paper>
                    <Form form={form} onSubmit={handleSubmit}>
                        <Box display="flex" flexDirection="column" alignItems="center">
                            <Stack spacing={4} p={isMobile ? 2 : 5} width={"100%"}>
                                <Alert variant="hint">
                                    Please enter the information below to gain access to the form
                                </Alert>

                                {isDobAuth ? (
                                    <ControlledDatePicker
                                        label={labelText}
                                        name="credentials"
                                        format={cultureDateFormat || undefined}
                                        fullWidth
                                        dataTestIdSuffix="credentials"
                                        open={pickerOpen}
                                        onOpen={() => setPickerOpen(true)}
                                        onClose={() => setPickerOpen(false)}
                                        slotProps={{
                                            textField: {
                                                onClick: () => setPickerOpen(true),
                                            },
                                        }}
                                    />
                                ) : (
                                    <ControlledTextField
                                        label={labelText}
                                        placeholder={labelText}
                                        name="credentials"
                                        type="text"
                                        fullWidth
                                        dataTestIdSuffix="credentials"
                                    />
                                )}

                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={isPending}
                                    sx={{
                                        alignSelf: "flex-end",
                                    }}
                                    size="small"
                                    data-test-id="submit_button"
                                >
                                    Continue
                                </Button>
                            </Stack>
                        </Box>
                    </Form>
                </Paper>
            </Stack>
        </Content>
    );
}
